import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { IDENTIFIER } from '../theme/tokens';

// Types are defined here (the reusable view's home) and re-exported by
// BulkDeviceActionDialog for backwards compatibility with existing imports.

export interface DeviceActionResultRow {
  serialNumber: string;
  result: string;
  detail: string | null;
  intent: 'success' | 'warning' | 'danger';
}

export interface DeviceActionResult {
  rows: DeviceActionResultRow[];
  summaryMessage: string | null;
}

export interface BulkActionResultViewProps {
  result: DeviceActionResult;
  // Optional short help line rendered below the result table. Assign/Remove
  // dialogs use it to tell users about the "Reset" / "Remove more" action;
  // ConfirmRemoveDevicesModal omits it because its footer is self-explanatory.
  resetLabel?: string;
  // Header for the key column. Defaults to 'Serial' (device flows); the
  // identity-mapping save results (FD-098) relabel it 'User'.
  keyColumnHeader?: string;
}

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
    minWidth: '480px',
  },
  countLine: {
    fontSize: '12px',
    color: '#52525b',
  },
  summaryStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: '8px',
    rowGap: '8px',
  },
  resultSurface: {
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    borderBottomLeftRadius: '6px',
    borderBottomRightRadius: '6px',
    maxHeight: '320px',
    overflowY: 'auto',
  },
  serialCell: {
    ...IDENTIFIER,
  },
  resultCell: {
    whiteSpace: 'nowrap',
  },
  resultCellInner: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
  },
  success: { color: '#16a34a' },
  warning: { color: '#a16207' },
  danger: { color: '#b91c1c' },
  rowWarning: { backgroundColor: '#fffbeb' },
  rowDanger: { backgroundColor: '#fef2f2' },
});

// Renders the intent-tinted summary + per-row result table shared by
// AssignDevicesDialog, the upcoming ConfirmRemoveDevicesModal, and any
// future bulk-action dialog. Extracted from BulkDeviceActionDialog so the
// new modal can reuse it without duplication.
export default function BulkActionResultView({
  result,
  resetLabel,
  keyColumnHeader = 'Serial',
}: BulkActionResultViewProps) {
  const styles = useStyles();

  const intentTextClass = (intent: DeviceActionResultRow['intent']) => {
    if (intent === 'success') return styles.success;
    if (intent === 'warning') return styles.warning;
    return styles.danger;
  };

  const rowTintClass = (intent: DeviceActionResultRow['intent']) => {
    if (intent === 'warning') return styles.rowWarning;
    if (intent === 'danger') return styles.rowDanger;
    return undefined;
  };

  const summary = summarizeResult(result.rows);

  return (
    <div className={styles.body}>
      {result.summaryMessage && (
        <div className={styles.countLine}>{result.summaryMessage}</div>
      )}
      {summary.length > 0 && (
        <div className={styles.summaryStrip}>
          {summary.map((group) => (
            <Badge
              key={group.label}
              appearance="tint"
              color={intentBadgeColor(group.intent)}
              icon={<IntentIcon intent={group.intent} />}
            >
              {group.count} {group.label}
            </Badge>
          ))}
        </div>
      )}
      <div className={styles.resultSurface}>
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{keyColumnHeader}</TableHeaderCell>
              <TableHeaderCell>Result</TableHeaderCell>
              <TableHeaderCell>Detail</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.rows.map((row) => (
              <TableRow
                key={`${row.serialNumber}-${row.result}`}
                className={rowTintClass(row.intent)}
              >
                <TableCell className={styles.serialCell}>{row.serialNumber}</TableCell>
                <TableCell
                  className={mergeClasses(styles.resultCell, intentTextClass(row.intent))}
                >
                  <span className={styles.resultCellInner}>
                    <IntentIcon intent={row.intent} />
                    {row.result}
                  </span>
                </TableCell>
                <TableCell>{row.detail ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {resetLabel && (
        <div className={styles.countLine}>
          Click <strong>{resetLabel}</strong> to submit another batch, or{' '}
          <strong>Close</strong> to return to the migration.
        </div>
      )}
    </div>
  );
}

interface SummaryGroup {
  intent: DeviceActionResultRow['intent'];
  label: string;
  count: number;
}

function summarizeResult(rows: DeviceActionResultRow[]): SummaryGroup[] {
  // Group by intent, take the first row's result string as the chip label so
  // "Removed" vs "Assigned" comes through without the primitive needing to know
  // which action is running. Preserve insertion order so success chips render
  // before warnings / dangers.
  const order: DeviceActionResultRow['intent'][] = ['success', 'warning', 'danger'];
  const groups = new Map<string, SummaryGroup>();
  for (const row of rows) {
    const existing = groups.get(row.intent);
    if (existing) {
      existing.count += 1;
    } else {
      groups.set(row.intent, { intent: row.intent, label: row.result, count: 1 });
    }
  }
  return order.map((i) => groups.get(i)).filter((g): g is SummaryGroup => Boolean(g));
}

function intentBadgeColor(intent: DeviceActionResultRow['intent']) {
  if (intent === 'success') return 'success';
  if (intent === 'warning') return 'warning';
  return 'danger';
}

function IntentIcon({ intent }: { intent: DeviceActionResultRow['intent'] }) {
  if (intent === 'success') return <CheckCircle2 size={14} strokeWidth={1.5} />;
  if (intent === 'warning') return <AlertTriangle size={14} strokeWidth={1.5} />;
  return <XCircle size={14} strokeWidth={1.5} />;
}
